import React, { useEffect, useState, useCallback } from 'react';
import { useFormik, FieldArray, FormikProvider, FormikErrors } from 'formik';
import * as yup from 'yup';
import InputField from 'components/shared/input-field';
import { FiX } from 'react-icons/fi';
import Button from 'components/shared/button';
import CountrySelect from 'components/shared/select-input/countryInput';
import SelectInput from 'components/shared/select-input';
import useFetchStates from 'hooks/useFetchStates';
import { formatWithCommas } from 'functions/currencyConveter';
import CustomFileUpload from 'components/shared/input-field/FileUpload';

interface EducationInfo {
  EducationLevel: string;
  CourseOfStudy: string;
  InstitutionName: string;
  InstitutionAddress: string;
  InstitutionAddress1: string;
  InstitutionAddress2: string;
  City: string;
  State: string;
  PostalCode: string;
  Country: string;
  TuitionFees: string;
  LoanAmount: string;
  TuitionFeeInvoice: File | null;
  OfficialSchoolID: File | null;
  utilityBill: File | null;
  validId: File | null;
}

interface Step2Props {
  inputData: EducationInfo;
  setInputData: (data: EducationInfo) => void;
  errors: { [key: string]: string };
  clearErrors: (field: string) => void;
  nextStep: () => void;
  prevStep: () => void;
}

const MySelfStep1: React.FC<Step2Props> = ({
  inputData,
  setInputData,
  errors,
  clearErrors,
  nextStep,
  prevStep,
}) => {
  const { data: stateOptions } = useFetchStates();

  const formik = useFormik({
    initialValues: inputData,
    validationSchema: yup.object({
      EducationLevel: yup.string().required('Education level is required'),
      CourseOfStudy: yup.string().required('Course of study is required'),
      InstitutionName: yup.string().required('Institution name is required'),
      InstitutionAddress: yup
        .string()
        .required('Institution address is required'),

      City: yup.string().required('City is required'),
      State: yup.string().required('State is required'),
      Country: yup.string().required('Country is required'),
      TuitionFees: yup.string().required('Tuition fees are required'),
      LoanAmount: yup.string().required('Loan amount is required'),
      TuitionFeeInvoice: yup
        .mixed()
        .required('Tuition fee invoice is required')
        .test(
          'fileSize',
          'File size is too large. Maximum size is 5MB.',
          (file: any) => (file ? file.size <= 5 * 1024 * 1024 : true)
        ),
      OfficialSchoolID: yup
        .mixed()
        .required('Official school ID is required')
        .test(
          'fileSize',
          'File size is too large. Maximum size is 5MB.',
          (file: any) => (file ? file.size <= 5 * 1024 * 1024 : true)
        ),
      utilityBill: yup
        .mixed()

        .required('Utility bill is required')
        .test(
          'fileSize',
          'File size is too large. Maximum size is 5MB.',
          (file: any) => {
            return file ? file.size <= 5 * 1024 * 1024 : true;
          }
        ),
      validId: yup
        .mixed()
        .required('Valid Id is required')
        .test(
          'fileSize',
          'File size is too large. Maximum size is 5MB.',
          (file: any) => {
            return file ? file.size <= 5 * 1024 * 1024 : true;
          }
        ),
    }),
    onSubmit: (values) => {
      setInputData(values);
      clearErrors('');
    },
  });

  useEffect(() => {
    setInputData(formik.values);
  }, [formik.values]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index?: number
  ) => {
    const { name, value } = e.target;

    const plainValue = value.replace(/,/g, '');
    if (name === `TuitionFees` && index !== undefined) {
      const plainValue = value.replace(/,/g, '');
      const formattedValue = plainValue ? formatWithCommas(plainValue) : '';
      formik.setFieldValue(`TuitionFees`, formattedValue);
    } else if (name === `LoanAmount` && index !== undefined) {
      const formattedValue = formatWithCommas(plainValue);
      formik.setFieldValue(`LoanAmount`, formattedValue);
      formik.handleChange(e);
    } else {
      formik.handleChange(e);
    }

    clearErrors(name);
  };

  const onDropUtilityBill = useCallback(
    (acceptedFiles: File[]) => {
      const currentFile = formik.values?.utilityBill || null;
      // Process the new file
      const filteredFile = acceptedFiles.find(
        (file) => file.size <= 5 * 1024 * 1024
      );
      if (!filteredFile) {
        formik.setFieldError(
          'utilityBill',
          'File size is too large. Maximum size is 5MB.'
        );
        formik.setFieldTouched('utilityBill', true, false);
      } else if (filteredFile && currentFile !== null) {
        formik.setFieldError('utilityBill', 'You can only upload one payslip.');
        formik.setFieldTouched('utilityBill', true, false);
        return;
      } else {
        formik.setFieldValue('utilityBill', filteredFile);
        formik.setFieldError('utilityBill', '');
      }
    },
    [formik]
  );

  const onDropValidId = useCallback(
    (acceptedFiles: File[]) => {
      const currentFile = formik.values?.validId || null;
      // Process the new file
      const filteredFile = acceptedFiles.find(
        (file) => file.size <= 5 * 1024 * 1024
      );
      if (!filteredFile) {
        formik.setFieldError(
          'validId',
          'File size is too large. Maximum size is 5MB.'
        );
        formik.setFieldTouched('validId', true, false);
      } else if (filteredFile && currentFile !== null) {
        formik.setFieldError('validId', 'You can only upload one payslip.');
        formik.setFieldTouched('validId', true, false);
        return;
      } else {
        formik.setFieldValue('validId', filteredFile);
        formik.setFieldError('validId', '');
      }
    },
    [formik]
  );

  const onDropInvoice = useCallback(
    (acceptedFiles: File[]) => {
      const currentFile = formik.values?.TuitionFeeInvoice || null;
      console.log(currentFile, 'currentFile');
      // Process the new file
      const filteredFile = acceptedFiles.find(
        (file) => file.size <= 5 * 1024 * 1024
      );
      if (!filteredFile) {
        formik.setFieldError(
          'TuitionFeeInvoice',
          'File size is too large. Maximum size is 5MB.'
        );
        formik.setFieldTouched('TuitionFeeInvoice', true, false);
      } else if (filteredFile && currentFile !== null) {
        formik.setFieldError(
          'TuitionFeeInvoice',
          'You can only upload one invoice.'
        );
        formik.setFieldTouched('TuitionFeeInvoice', true, false);
        return;
      } else {
        formik.setFieldValue('TuitionFeeInvoice', filteredFile);
        formik.setFieldError('TuitionFeeInvoice', '');
      }
    },
    [formik]
  );

  const onDropSchoolId = useCallback(
    (acceptedFiles: File[]) => {
      const currentFile = formik.values?.OfficialSchoolID || null;
      // Process the new file
      const filteredFile = acceptedFiles.find(
        (file) => file.size <= 5 * 1024 * 1024
      );
      if (!filteredFile) {
        formik.setFieldError(
          'OfficialSchoolID',
          'File size is too large. Maximum size is 5MB.'
        );
        formik.setFieldTouched('OfficialSchoolID', true, false);
      } else if (filteredFile && currentFile !== null) {
        formik.setFieldError(
          'OfficialSchoolID',
          'You can only upload one invoice.'
        );
        formik.setFieldTouched('OfficialSchoolID', true, false);
        return;
      } else {
        formik.setFieldValue('OfficialSchoolID', filteredFile);
        formik.setFieldError('OfficialSchoolID', '');
      }
    },
    [formik]
  );

  const removeDocument = (type: keyof EducationInfo) => {
    formik?.setFieldValue(type, null);
    formik.setFieldTouched(type, false, false);
    formik.setFieldError(type, '');
  };

  const educationLevels = [
    { value: 'under graduate', label: 'Undergraduate' },
    { value: 'post graduate', label: 'Postgraduate' },
    { value: 'executive mba', label: 'Executive MBA' },
    { value: 'mini mba', label: 'Mini MBA' },
    { value: 'short course', label: 'Short Course' },
  ];

  return (
    <div className="w-full  scrollbar-always-visible">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl fomt-semibold">Education Details</h1>
        <p className="text-lg text-gray-2">Complete the fields below</p>
      </div>
      <hr className="my-4" />
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <>
            <div className="w-full p-4 rounded grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <div className="col-span-1 state-input">
                <SelectInput
                  label="Education Level"
                  name="EducationLevel"
                  options={educationLevels}
                  placeholder="Select education level..."
                  value={
                    educationLevels.find(
                      (option) =>
                        option?.value === formik?.values?.EducationLevel
                    ) || null
                  }
                  onChange={(name, value) =>
                    formik?.setFieldValue(name, value?.value)
                  }
                  error={formik.errors.EducationLevel}
                />
              </div>
              <div className="col-span-1">
                <InputField
                  label="Course Of Study"
                  type="text"
                  name={`CourseOfStudy`}
                  placeholder="Enter the course you studied"
                  value={formik.values.CourseOfStudy}
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  required
                  error={formik.errors.CourseOfStudy}
                />
              </div>
              <div className="col-span-1">
                <InputField
                  type="text"
                  label="Institution Name"
                  name={`InstitutionName`}
                  placeholder="Enter institution Name"
                  value={formik.values.InstitutionName}
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  required
                  error={formik.errors.InstitutionName}
                />
              </div>
              <div className="col-span-1">
                <InputField
                  type="text"
                  label="Institution Address"
                  name={`InstitutionAddress`}
                  placeholder="Enter institution address"
                  value={formik.values.InstitutionAddress}
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  required
                  error={formik.errors.InstitutionAddress}
                />
              </div>
              <div className="col-span-1">
                <InputField
                  label="Institution Address 1(Optional)"
                  type="text"
                  name={`InstitutionAddress1`}
                  placeholder="Enter institution address1"
                  value={formik.values.InstitutionAddress1}
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  error={formik.errors.InstitutionAddress1}
                />
              </div>
              <div className="col-span-1">
                <InputField
                  label="Institution Address 2(Optional)"
                  type="text"
                  name={`InstitutionAddress2`}
                  placeholder="Enter institution address2"
                  value={formik.values.InstitutionAddress2}
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  error={formik.errors.InstitutionAddress2}
                />
              </div>
              <CountrySelect
                name={`Country`}
                value={formik?.values?.Country}
                setFieldValue={formik.setFieldValue}
                onChange={handleInputChange}
                placeholder="Choose Country"
                required={true}
              />

              <div className="col-span-1">
                <InputField
                  type="text"
                  label="City"
                  name={`City`}
                  placeholder="e.g Lagos"
                  value={formik.values.City}
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  required
                  error={formik.errors.City}
                />
              </div>
              <div className="col-span-1 state-input">
                <SelectInput
                  label="State/Province/Region"
                  options={stateOptions}
                  required={true}
                  placeholder="Select State..."
                  name={`State`}
                  value={
                    stateOptions.find(
                      (option) => option?.label === formik?.values?.State
                    ) || null
                  }
                  onChange={(name, value) => {
                    formik.setFieldValue(name, value?.label);
                  }}
                  error={formik.errors.State}
                />
              </div>

              <div className="col-span-1">
                <InputField
                  type="text"
                  label="Zip/Postal Code"
                  name={`PostalCode`}
                  placeholder="e.g 101245"
                  value={formik.values.PostalCode}
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  // error={formik.errors.PostalCode}
                />
              </div>

              <div className="col-span-1">
                <InputField
                  type="text"
                  label="Tuition Fee"
                  name={`TuitionFees`}
                  placeholder="Tuition Fee"
                  value={formatWithCommas(formik.values.TuitionFees.toString())}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange(e)
                  }
                  onBlur={formik.handleBlur}
                  required
                  error={formik.errors.TuitionFees}
                />
              </div>

              <div className="col-span-1">
                <InputField
                  type="text"
                  label="Loan Amount"
                  name={`LoanAmount`}
                  placeholder="Loan amount"
                  value={formatWithCommas(formik.values.LoanAmount.toString())}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange(e)
                  }
                  onBlur={formik.handleBlur}
                  required
                  error={formik.errors.LoanAmount}
                />
              </div>
              {/* Upload Section */}

              <CustomFileUpload
                label="Tuition Fee Invoice "
                formik={formik}
                name="TuitionFeeInvoice"
                removeDocument={removeDocument}
                callbackfn={onDropInvoice}
              />
              <CustomFileUpload
                label="Official School ID "
                description="(Registration form, ID or any form of representation that confirms you have started or completed registration in this institution)"
                formik={formik}
                name="OfficialSchoolID"
                removeDocument={removeDocument}
                callbackfn={onDropSchoolId}
              />
              <CustomFileUpload
                label="Utility Bill"
                formik={formik}
                name="utilityBill"
                removeDocument={removeDocument}
                callbackfn={onDropUtilityBill}
                maxSizeLabel="5MB"
              />
              <CustomFileUpload
                label="Valid ID"
                description="(International Passport, NIN Slip, Driver's Licence, or PVC)"
                formik={formik}
                name="validId"
                removeDocument={removeDocument}
                callbackfn={onDropValidId}
                maxSizeLabel="5MB"
              />
            </div>
            <div className="flex justify-end gap-3 float-end">
              <Button
                type="button"
                disabled={
                  !formik.values.EducationLevel ||
                  !formik.values.CourseOfStudy ||
                  !formik.values.InstitutionName ||
                  !formik.values.InstitutionAddress ||
                  !formik.values.City ||
                  !formik.values.Country ||
                  !formik.values.TuitionFees ||
                  !formik.values.LoanAmount ||
                  !formik.values.TuitionFeeInvoice ||
                  !formik.values.validId ||
                  !formik.values.utilityBill ||
                  !formik.values.OfficialSchoolID
                }
                onClick={() => nextStep()}
                className="w-full"
              >
                Next
              </Button>
            </div>
          </>
        </form>
      </FormikProvider>
    </div>
  );
};

export default MySelfStep1;
